/**
 * Calcul des totaux d'un devis.
 *
 * Fonction pure, sans I/O : c'est elle qui décide de l'argent affiché au
 * client, elle doit donc être testable seule et donner le même résultat quelle
 * que soit la porte qui l'appelle.
 *
 * ══════════════════════════════════════════════════════════════
 * Elle recopie l'arithmétique de la base, pas seulement son résultat
 * ══════════════════════════════════════════════════════════════
 * Postgres recalcule ces mêmes totaux par trigger sur `quote_lines`, et les
 * deux doivent concorder au centime. Concorder ne suffit pas à viser le même
 * nombre : il faut arrondir **au même endroit**.
 *
 *   · `quote_lines.total_ht` et `total_cost` sont des colonnes générées
 *     `DECIMAL(12,2)` **stockées** : la base arrondit chaque ligne, puis
 *     additionne. On fait de même, ligne par ligne ;
 *   · la TVA se somme sur ce HT déjà arrondi, comme
 *     `SUM(total_ht * tva_rate / 100)` la somme sur la colonne stockée ;
 *   · le TTC, la marge et son pourcentage se calculent sur les totaux
 *     arrondis, comme le second `UPDATE` du trigger qui relit les colonnes
 *     qu'il vient d'écrire.
 *
 * Sommer sans arrondir puis arrondir une seule fois — ce que faisait ce
 * fichier — donnait un centime d'écart dès qu'un produit dépassait deux
 * décimales. Trois lignes de 1,5 × 10,99 € rendaient 49,46 ici et 49,47 en
 * base. `facturer_et_envoyer` compare le montant annoncé au montant créé et
 * refuse d'envoyer sur l'écart : la facture restait en brouillon, et le
 * garde-fou devenait le point de panne.
 *
 * Le même défaut rendait HT + TVA ≠ TTC à un centime près, puisque chaque
 * terme était arrondi séparément d'une somme qui ne l'était pas. Sur une
 * soumission Factur-X ou Chorus Pro, cette incohérence est un motif de rejet.
 */
import type { QuoteLine, QuoteTotals } from "./types.js";
import { round2 } from "../../money.js";

export function computeQuoteTotals(
  lines: readonly QuoteLine[],
  tvaOverride?: number,
): QuoteTotals {
  let cumulHt = 0;
  let cumulTva = 0;
  let cumulCost = 0;

  for (const line of lines) {
    // Les en-têtes de section structurent le document mais ne portent aucun
    // montant : les compter gonflerait le total du devis.
    if (line.isSectionHeader) continue;

    const ht = round2(line.quantity * line.unitPrice);
    const cost = round2(line.quantity * line.unitCost);
    const rate = (tvaOverride ?? line.tvaRate) / 100;

    cumulHt += ht;
    cumulTva += ht * rate;
    cumulCost += cost;
  }

  const totalHt = round2(cumulHt);
  const totalTva = round2(cumulTva);
  const totalCost = round2(cumulCost);
  const marginAmount = round2(totalHt - totalCost);

  return {
    totalHt,
    totalTva,
    totalTtc: round2(totalHt + totalTva),
    totalCost,
    marginAmount,
    // Deux décimales, comme `quotes.margin_percent DECIMAL(5,2)`. L'écran
    // formate à une décimale de son côté (`formatPercent`), l'affichage ne
    // change donc pas.
    //
    // Une marge sur un total nul n'est pas 0 % au sens comptable, mais
    // l'afficher ainsi vaut mieux qu'un NaN dans l'interface.
    marginPercent: totalHt > 0 ? round2((marginAmount / totalHt) * 100) : 0,
  };
}
